/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
import { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import PrivateRoute from './components/PrivateRoute';
import Header from './components/Common/Header';
import Sidebar from './components/Common/Sidebar';
import RouteErrorBoundary from './components/Common/RouteErrorBoundary';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SearchPage from './pages/SearchPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import RegisterPage from './pages/RegisterPage';

// 以下頁面體積較大（react-markdown / react-pdf / x-date-pickers / date-fns 等重量依賴的
// 消費點），改路由層 React.lazy 分割，減少初始載入 JS 體積（見 docs/plans/2026-08-14-frontend-build-warnings-fix.md Track B）
const UploadPage = lazy(() => import('./pages/UploadPage'));
const DocumentViewPage = lazy(() => import('./pages/DocumentViewPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const VideoDownloadPage = lazy(() => import('./pages/VideoDownloadPage'));
const DocumentManagementPage = lazy(() => import('./pages/DocumentManagementPage'));
const TagSelectorTestPage = lazy(() => import('./pages/TagSelectorTestPage'));
const TestPreviewPage = lazy(() => import('./pages/TestPreviewPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const MyUploadsPage = lazy(() => import('./pages/MyUploadsPage'));
const DictionaryPage = lazy(() => import('./pages/DictionaryPage'));
const WordMergePage = lazy(() => import('./pages/WordMergePage'));

// lazy 頁面載入中顯示的畫面，樣式對齊 PrivateRoute.jsx 的載入圈
function RouteLoadingFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <CircularProgress />
    </Box>
  );
}

// 全域 Console 物件保護（防止 console 相關錯誤）
if (typeof window !== 'undefined') {
  if (!window.console) {
    window.console = {};
  }

  const consoleMethods = [
    'log',
    'warn',
    'error',
    'info',
    'debug',
    'trace',
    'table',
    'clear',
    'count',
    'group',
    'groupEnd',
  ];
  consoleMethods.forEach((method) => {
    if (!window.console[method] || typeof window.console[method] !== 'function') {
      window.console[method] = function () {
        // 安全的空函數
      };
    }
  });

  // 確保 console 物件不會被意外覆寫
  try {
    Object.defineProperty(window.console, 'logs', {
      get: function () {
        return [];
      },
      configurable: false,
    });
  } catch (e) {
    // 忽略定義錯誤
  }
}

// 內部 App 元件（使用 AuthContext）
function AppContent() {
  const { isAuthenticated, user, logout, loading } = useAuth();

  // 側邊欄開關狀態
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  // Toggle 側邊欄
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // 如果認證狀態還在載入中，顯示載入畫面
  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        載入中...
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* 只有登入後才顯示 Header 和 Sidebar */}
      {isAuthenticated && (
        <>
          {/* 頂部導覽列 - 固定在最上方 */}
          <Header user={user} onLogout={handleLogout} onMenuClick={handleDrawerToggle} />

          {/* 側邊欄 - 桌面版和手機版都使用 temporary（抽屜式） */}
          <Sidebar user={user} open={drawerOpen} onClose={handleDrawerToggle} variant="temporary" />
        </>
      )}

      {/* 主要內容區域 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // 外層 row flex 的 item 預設 min-width:auto（＝內容 min-content 寬），窄螢幕下
          // main 拒絕收縮會讓整頁橫向溢出，把頁內工具列按鈕推出 viewport；設 0 才允許收縮
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          marginTop: isAuthenticated ? '64px' : 0, // 只有登入後才有 AppBar 高度
          marginLeft: 0,
        }}
      >
        {/* 路由內容 */}
        <Box sx={{ flexGrow: 1 }}>
          <RouteErrorBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                {/* 公開路由 */}
                <Route
                  path="/login"
                  element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
                />
                <Route
                  path="/register"
                  element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />}
                />
                <Route
                  path="/forgot-password"
                  element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
                />
                <Route
                  path="/reset-password/:token"
                  element={isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />}
                />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />

                {/* 需要登入的路由 */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <HomePage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/search"
                  element={
                    <PrivateRoute>
                      <SearchPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/document/:id"
                  element={
                    <PrivateRoute>
                      <DocumentViewPage />
                    </PrivateRoute>
                  }
                />

                {/* 需要進階使用者權限的路由 */}
                <Route
                  path="/upload"
                  element={
                    <PrivateRoute requiredRole="advanced">
                      <UploadPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/my-uploads"
                  element={
                    <PrivateRoute requiredRole="advanced">
                      <MyUploadsPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/upload/word-merge"
                  element={
                    <PrivateRoute requiredRole="advanced">
                      <WordMergePage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/dictionary"
                  element={
                    <PrivateRoute requiredRole="advanced">
                      <DictionaryPage />
                    </PrivateRoute>
                  }
                />

                {/* 需要管理員權限的路由 */}
                <Route
                  path="/admin"
                  element={
                    <PrivateRoute requiredRole="admin">
                      <AdminPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/video-download"
                  element={
                    <PrivateRoute requiredRole="admin">
                      <VideoDownloadPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/admin/documents"
                  element={
                    <PrivateRoute requiredRole="admin">
                      <DocumentManagementPage />
                    </PrivateRoute>
                  }
                />

                {/* 個人資料頁面 */}
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <ProfilePage />
                    </PrivateRoute>
                  }
                />

                {/* 測試路由（開發用，需要登入） */}
                <Route
                  path="/test/tag-selector"
                  element={
                    <PrivateRoute>
                      <TagSelectorTestPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/test/preview"
                  element={
                    <PrivateRoute>
                      <TestPreviewPage />
                    </PrivateRoute>
                  }
                />

                {/* 預設重導向 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}

// 主 App 元件（包裹 AuthProvider）
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
